<script lang="ts">
  import { schemaData } from '$shared/lib/data/SchemaData';
  import Input from '$shared/atoms/Input/Input.svelte';
  import InputGroup from '$shared/atoms/InputGroup/InputGroup.svelte';
  import AutoComplete from '$shared/atoms/AutoComplete/AutoComplete.svelte';
  import Dropdown from '$shared/atoms/Dropdown/Dropdown.svelte';
  import AddressType, {
    getAddressTypeAsList,
    MapAddressTypeLabel
  } from '$provider/lib/apis/models/AddressType';
  import type { IAddress } from '$provider/lib/apis/interfaces/IResponse';
  import { onMount } from 'svelte';

  interface Props {
    label?: string;
    data: IAddress;
    addressErrors?: any;
    showType?: boolean;
    onchange?: (data: IAddress) => void;
  }

  let {
    label = '',
    data,
    addressErrors = $bindable({}),
    showType = false,
    onchange
  }: Props = $props();

  export const set = (address: IAddress): void => {
    prepareFrom(address);
  };
  let selectedState = $state('');
  let stateList = schemaData.getStateAsCommonlist();

  enum Fields {
    addressLine1 = 'addressLine1',
    addressLine2 = 'addressLine2',
    city = 'city',
    state = 'state',
    zipCode = 'zipCode',
    addressType = 'addressType'
  }

  let formData: IAddress = $state({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: null,
    zipCode: '',
    type: AddressType.Work
  });

  onMount(() => {
    prepareFrom(data);
  });

  const prepareFrom = (address: IAddress): void => {
    if (!address) {
      return;
    }
    formData.addressLine1 = address.addressLine1 || '';
    formData.addressLine2 = address.addressLine2 || '';
    formData.city = address.city || '';
    formData.state = address.state || ''; // state should be id of state from stateList
    formData.zipCode = address.zipCode || '';
    if (formData.state) {
      const tempSelectedState = stateList.find((state) => state?.id === formData.state);
      selectedState = tempSelectedState?.label || '';
    }
    address.type && (formData.type = address.type);
  };

  const dispatchChange = (): void => {
    onchange?.(formData);
  };

  const onInputTextChange = (target: string, detail: { value: string }) => {
    formData[target] = detail.value?.trim();
    dispatchChange();
  };

  const onStateUpdate = (item: CommonList) => {
    selectedState = item.label;
    formData.state = item.id;
    dispatchChange();
  };

  const onStateCleared = () => {
    selectedState = '';
    formData.state = '';
    dispatchChange();
  };

  const onAddressTypeChange = (item: CommonList): void => {
    formData.type = item.id as AddressType;
    dispatchChange();
  };
</script>

<div class="comp-address-input">
  <!-- Address Line 1 -->
  <div class="input-wrap">
    <Input
      required={false}
      {label}
      name={Fields.addressLine1}
      placeholder="Address Line 1"
      value={formData?.addressLine1 || ''}
      bind:error={
        () => addressErrors.addressLine1 ?? null, (val) => (addressErrors.addressLine1 = val)
      }
      oninput={(detail) => onInputTextChange('addressLine1', detail)}
    />
  </div>
  <!-- /Address Line 1 -->

  <!-- Address Line 2 -->
  <div class="input-wrap">
    <Input
      required={false}
      name={Fields.addressLine2}
      placeholder="Address Line 2 (Optional)"
      value={formData?.addressLine2 || ''}
      oninput={(detail) => onInputTextChange('addressLine2', detail)}
    />
  </div>
  <!-- /Address Line 2 -->

  <InputGroup>
    <!-- City -->
    <div class="input-wrap">
      <Input
        required={false}
        placeholder="City"
        name={Fields.city}
        value={formData?.city || ''}
        bind:error={() => addressErrors.city ?? null, (val) => (addressErrors.city = val)}
        oninput={(detail) => onInputTextChange('city', detail)}
      />
    </div>
    <!-- /City -->

    <!-- State -->
    <div class="input-wrap">
      <AutoComplete
        value={selectedState}
        required={false}
        placeholder="State"
        name={Fields.state}
        list={stateList}
        bind:error={() => addressErrors.state ?? null, (val) => (addressErrors.state = val)}
        onclick={(item) => onStateUpdate(item)}
        oncleared={() => onStateCleared()}
      />
    </div>
    <!-- /State -->
  </InputGroup>

  <!-- Zip Code -->
  <InputGroup>
    <div class="input-wrap">
      <Input
        required={false}
        placeholder="Zip Code"
        name={Fields.zipCode}
        value={formData?.zipCode || ''}
        bind:error={() => addressErrors.zipCode ?? null, (val) => (addressErrors.zipCode = val)}
        oninput={(detail) => onInputTextChange('zipCode', detail)}
      />
    </div>
  </InputGroup>
  <!-- Zip Code -->

  <!-- Address Type  -->
  <InputGroup>
    {#if showType}
      <div class="input-wrap">
        <div class="input-label">Address Type</div>
        <Dropdown
          islarge
          name={Fields.addressType}
          label={MapAddressTypeLabel[formData.type || AddressType.Work]}
          maxWidth={true}
          list={getAddressTypeAsList()}
          onchange={onAddressTypeChange}
        />
      </div>
    {/if}
  </InputGroup>
  <!-- Address Type  -->
</div>

<style>
  .input-wrap {
    margin-bottom: 24px;
  }
  .input-label {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: var(--black);
    margin-bottom: 4px;
  }
</style>
